import { ApiError, VibratoClient } from "@/lib/vibrato-api"
import usertour from "usertour.js"
// import ReactPullToRefresh from "react-pull-to-refresh"
import { App } from "@capacitor/app"
import { Browser } from "@capacitor/browser"
import React, { useContext, useEffect, useRef } from "react"
import { GlobalUserContext } from "../GlobalLayout"
import { GetTokenSilentlyVerboseResponse } from "@auth0/auth0-spa-js"
import { NATIVE_DEVICE_ID, settings } from "@/settings"
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react"
import * as Sentry from "@sentry/react"
import { ChatBubble } from "iconoir-react"
import LogRocket from "logrocket"
import mixpanel from "mixpanel-browser"
import { useCallback, useState } from "react"
import { useNavigate } from "react-router"
import { Outlet, useLocation, useSearchParams } from "react-router-dom"
import LoadingFullPage, { LoadingMobileLogin } from "../Loading"
import Logout from "../logout/Logout"
import {
  AppleInAppPurchasesContext,
  IntroModalContext,
  NotificationsContext,
  PushTokenContext,
  UpdateUserContext,
  UpgradeModalContext,
  UserContext,
  VibratoClientContext
} from "./contexts"
import CountryRestriction from "./CountryRestriction"
import EmailNotVerified from "./EmailNotVerified"
import ErrorPage from "./ErrorPage"
import IntroModal from "./IntroModal"
import Nav from "./Nav"
import Notifications from "./Notifications"
import PricingTableModal from "./PricingTableModal"
import ReferralRewardModal from "./ReferralRewardModal"
import VerifyPhoneNumberModal from "./VerifyPhoneNumberModal"
import { MobileSplashPage } from "../splash/MobileSplashPage"
import useReloadOnNewPlan from "@/hooks/useReloadOnNewPlan"
import { useRegisterPushToken } from "@/hooks/useRegisterPushToken"
import GlobalAlert from "./GlobalAlert"
import { useAppleSubs } from "../AppleSubs"
import { getReferralUuid, clearReferralUuid } from "../Referral"
import { getGiftUuid, clearGiftUuid } from "../Gift"
import ReviewPrompt from "./ReviewPrompt"
import { getPromoCode, clearPromoCode } from "@/lib/promo"

const defaultRedirectUrl = new URL("/dashboard", window.location.origin).toString()

/**
 * Helper function to detect if an Auth0 error indicates refresh token expiration
 * @param error The error from getAccessTokenSilently
 * @returns true if the error indicates refresh token expiration
 */
function isRefreshTokenExpired(error: any): boolean {
  // Auth0 specific error patterns for refresh token expiration
  return (
    error?.error === "login_required" ||
    error?.error === "consent_required" ||
    error?.error_description?.toLowerCase().includes("refresh token") ||
    error?.message?.toLowerCase().includes("refresh token") ||
    error?.message?.toLowerCase().includes("login_required") ||
    // Handle cases where the error is nested
    error?.originalError?.error === "login_required" ||
    error?.originalError?.error === "consent_required"
  )
}

export default function DashboardLayout(): any {
  const navigate = useNavigate()

  const onRedirectCallback = (appState: any) => {
    console.log("onRedirectCallback", appState.returnTo)
    let returnTo = appState.returnTo || window.location.pathname + window.location.search + window.location.hash
    if (returnTo.includes("#")) {
      returnTo += "&no_universal_links"
    } else {
      returnTo += "#no_universal_links"
    }

    // Skip navigation to logout or signup pages
    if (returnTo.startsWith("/logout") || returnTo.startsWith("/signup")) {
      navigate("/dashboard")
    } else {
      navigate(returnTo)
    }
  }

  if (settings.IS_NATIVE_APP) {
    const redirectUrl = `com.getvibrato.mobile://${settings.AUTH0_DOMAIN}/capacitor/com.getvibrato.mobile/callback`

    return (
      <Auth0Provider
        cacheLocation="localstorage"
        domain={settings.AUTH0_DOMAIN}
        clientId={settings.AUTH0_MOBILE_CLIENT_ID}
        onRedirectCallback={onRedirectCallback}
        useRefreshTokensFallback={false}
        useRefreshTokens
        authorizationParams={{
          redirect_uri: redirectUrl,
          audience: settings.AUTH0_MOBILE_AUDIENCE,
          scope: "openid profile email offline_access",
          prompt: "login"
        }}
      >
        <DashboardLayoutInner>
          <Outlet />
        </DashboardLayoutInner>
      </Auth0Provider>
    )
  }

  return (
    <Auth0Provider
      domain={settings.AUTH0_DOMAIN}
      clientId={settings.AUTH0_CLIENT_ID}
      onRedirectCallback={onRedirectCallback}
      authorizationParams={{
        redirect_uri: defaultRedirectUrl,
        audience: settings.AUTH0_AUDIENCE,
        scope: "openid profile email",
        prompt: "login"
      }}
    >
      <DashboardLayoutInner>
        <Outlet />
      </DashboardLayoutInner>
    </Auth0Provider>
  )
}

// function LogoutButton(): any {
//   const { logout } = useAuth0()

//   const returnTo = "https://www.getvibrato.com"
//   console.log(`Logout: ${returnTo}`)

//   return (
//     <button onClick={() => logout({ logoutParams: { returnTo } })}>
//       <div className="text-base font-normal text-gray-700">Log Out</div>
//     </button>
//   )
// }

async function setUserApiSession(
  getAccessTokenSilently: any,
  setVibratoClient: any,
  setUserSession: any,
  setShowCountryRestriction: any,
  setShowEmailVerification: any,
  setCcVerified: any,
  setShowIntroModal: any,
  navigate?: any,
  setProcessingGift?: any,
  setShowReferralRewardModal?: any,
  setErrorPageMsg?: any
): Promise<any> {
  async function getToken() {
    let token: GetTokenSilentlyVerboseResponse | null = null

    if (settings.IS_NATIVE_APP) {
      token = await getAccessTokenSilently({
        detailedResponse: true,
        authorizationParams: {
          audience: settings.AUTH0_MOBILE_AUDIENCE,
          scope: "openid profile email offline_access"
        }
      })
    } else {
      token = await getAccessTokenSilently({
        detailedResponse: true,
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: "openid profile email"
        }
      })
    }

    return token
  }

  let token: GetTokenSilentlyVerboseResponse | null = null

  try {
    token = await getToken()
  } catch (err) {
    console.error("Failed to get access token silently", err)

    if (isRefreshTokenExpired(err)) {
      console.log("Refresh token expired, forcing re-authentication")
      if (navigate) {
        navigate("/logout")
      }
      return
    }

    Sentry.captureException(err)

    // Handle other token errors (network issues, temporary failures, etc.)
    if (settings.IS_NATIVE_APP && navigate) {
      console.log("Navigating to mobile")
      navigate("/mobile")
    } else {
      console.log("Navigating to error page")
      const errorMessage = "There was an issue logging in to your account."
      setErrorPageMsg(errorMessage)
    }

    return
  }

  if (!token) {
    console.error("Failed to get access token silently")
    return
  }

  const refreshErrorCallback = async (error?: any) => {
    console.log("Refresh error callback triggered", error)

    if (error && isRefreshTokenExpired(error)) {
      console.log("Refresh token expired in callback, forcing re-authentication")
      if (navigate) {
        navigate("/logout")
      }
      return
    }

    Sentry.captureException(error)

    // Handle other refresh errors (fallback behavior)
    if (navigate) {
      if (settings.IS_NATIVE_APP) {
        navigate("/mobile")
      } else {
        const errorMessage = "There was an issue refreshing your session. Please log out and try logging in again."
        navigate(`/error?message=${encodeURIComponent(errorMessage)}`)
      }
    }
  }

  const client = new VibratoClient(token.access_token, token.expires_in, getToken, refreshErrorCallback)

  setVibratoClient(client)

  try {
    const session = await client.getUserApiSession()

    // Handle referral registration for freemium users
    if (session.plan === "freemium" && !session.referred_by) {
      const referralUuid = getReferralUuid()
      if (referralUuid) {
        try {
          console.log("Processing referral for freemium user:", referralUuid)

          const result = await client.registerReferral(referralUuid)

          if (result.success) {
            console.log("Referral registered successfully:", result)
            setShowReferralRewardModal(true)
          } else {
            console.error("Failed to register referral:", result.message)
          }
        } catch (error) {
          console.error("Error processing referral:", error)
        }

        clearReferralUuid()
      }
    }

    // Handle promo code registration
    const promoCode = getPromoCode()
    if (promoCode) {
      try {
        console.log("Applying promo code:", promoCode)
        const result = await client.applyPromoCode(promoCode)

        if (result.success) {
          console.log("Promo code applied successfully:", result)
          clearPromoCode()
        } else {
          console.error("Failed to apply promo code:", result.message)
        }
      } catch (error) {
        console.error("Error applying promo code:", error)
        Sentry.captureException(error)
      }
    }

    // Handle gift subscription registration for users
    const giftUuid = getGiftUuid()
    if (giftUuid) {
      var giftSuccess = false
      try {
        console.log("Processing gift subscription for user:", giftUuid)

        setProcessingGift(true)
        const result = await client.registerGiftSubscription(giftUuid)

        if (result.success) {
          console.log("Gift subscription registered successfully:", result)
          giftSuccess = true
        } else {
          console.error("Failed to register gift subscription:", result.message)
        }
      } catch (error) {
        console.error("Error processing gift subscription:", error)
      }

      clearGiftUuid()
      if (giftSuccess) {
        console.log("Gift subscription registered successfully, redirecting to new subscription")
        window.location.href = "/new-subscription"
      } else {
        setProcessingGift(false)
      }
    }

    console.log("Setting user session", session)
    setUserSession(session)
    if (session.cc_verified) {
      setCcVerified(true)
    }

    // if (!session.verified_phone_number) {
    //   setShowVerifyPhoneNumberModal(true);
    // }

    // const showIntroModal = window.localStorage.getItem(`show_intro_modal:${session.email}`)
    // if (session.show_intro_flow || showIntroModal === "true") {
    //   // setShowIntroModal(true)
    // }
    // if (showIntroModal === "false") {
    //   setShowIntroModal(false)
    // }
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get("intro_modal") === "1") {
      setShowIntroModal(true)
    }

    try {
      usertour.init("cmcl58jpx01xmo6e03yacevbj")
      usertour.identify(session.id, {
        email: session.email
      })

      mixpanel.identify(session.id)

      // Set user properties for platform information
      mixpanel.people.set({
        is_native_app: settings.IS_NATIVE_APP,
        platform: settings.IS_ANDROID ? "android" : settings.IS_IOS ? "ios" : "web",
        native_device_id: NATIVE_DEVICE_ID || null
      })

      LogRocket.identify(session.id, {
        email: session.email
      })
    } catch (err) {
      console.error("Failed to identify user", err)
    }
  } catch (err: any) {
    if (err.message.includes("Your email is not verified")) {
      setShowEmailVerification(true)
    } else if (err.message.includes("Vibrato is only available in certain countries")) {
      setShowCountryRestriction(true)
    } else {
      Sentry.captureException(err)

      // Check if ApiError
      var errorMessage = "There was an issue logging in to your account."
      if (err instanceof ApiError) {
        errorMessage = err.data?.detail || errorMessage
      }

      setErrorPageMsg(errorMessage)
    }
  }
}

const initialUpgradeModalHeader = "Intelligent Calling Solutions for Every Need"
const initialUpgradeModalDetails =
  "Our diverse plans offer everything from basic personal use to advanced business functionalities."
const initialUpgradeModalTitle = "Upgrade today"

function DashboardLayoutInner({
  children // will be a page or nested layout
}: {
  children: React.ReactNode
}): any {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const authInitiated = useRef(false)
  const { setEmail, setUserId } = useContext(GlobalUserContext)

  const [vibratoClient, setVibratoClient] = useState<any>(null)
  const [userSession, setUserSession] = useState<any>(null)
  const [showCountryRestriction, setShowCountryRestriction] = useState(false)
  const [showEmailVerification, setShowEmailVerification] = useState(false)
  const [, setCcVerified] = useState(false)
  const [notifs, setNotifs] = useState<any>([])
  const { isAuthenticated, getAccessTokenSilently, loginWithRedirect, isLoading, error, handleRedirectCallback } =
    useAuth0()

  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [errorPageMsg, setErrorPageMsg] = useState<string>("")
  const [upgradeModalError, setUpgradeModalError] = useState<string>("")
  const [upgradeModalTitle, setUpgradeModalTitle] = useState<string>(initialUpgradeModalTitle)
  const [upgradeModalHeader, setUpgradeModalHeader] = useState<string>(initialUpgradeModalHeader)
  const [upgradeModalDetails, setUpgradeModalDetails] = useState<string>(initialUpgradeModalDetails)
  const [upgradeModalInitialView, setUpgradeModalInitialView] = useState<string>("individual")
  const [showVerifyPhoneNumberModal, setShowVerifyPhoneNumberModal] = useState(false)
  const [showReferralRewardModal, setShowReferralRewardModal] = useState(false)
  const [showIntroModal, setShowIntroModal] = useState(false)
  const [processingGift, setProcessingGift] = useState(false)
  const appleInAppPurchases = useAppleSubs({ userSession, vibratoClient })

  // Reset auth state when component mounts
  useEffect(() => {
    authInitiated.current = false
  }, [])

  // Also reset if user becomes unauthenticated (e.g., logout, session expired)
  useEffect(() => {
    if (!isAuthenticated) {
      authInitiated.current = false
    } else {
      console.log("isAuthenticated", isAuthenticated)
    }
  }, [isAuthenticated])

  // Handle authentication for native app
  useEffect(() => {
    if (location.pathname === "/mobile") {
      console.log("Skipping native app authentication for /mobile route")
      return
    }

    if (!isAuthenticated && !isLoading && !authInitiated.current && settings.IS_NATIVE_APP) {
      authInitiated.current = true
      console.log("Not authenticated, attempting native login")

      const isSignupRoute = location.pathname === "/signup"
      const authorizationParams = isSignupRoute ? { screen_hint: "signup" } : {}

      loginWithRedirect({
        async openUrl(url) {
          await Browser.open({
            url,
            windowName: "_self"
          })
        },
        authorizationParams
      })
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, location.pathname])

  // Handle authentication for web app
  useEffect(() => {
    if (location.pathname === "/mobile") {
      console.log("Skipping native app authentication for /mobile route")
      return
    }

    if (!isAuthenticated && !isLoading && !authInitiated.current && !settings.IS_NATIVE_APP) {
      authInitiated.current = true
      const returnTo = window.location.pathname + window.location.search
      console.log("Not authenticated, attempting login, returnTo", returnTo)

      const isSignupRoute = location.pathname === "/signup"
      let authorizationParams = {}

      if (isSignupRoute) {
        const urlParams = new URLSearchParams(window.location.search)
        const email = urlParams.get("email")
        authorizationParams = {
          screen_hint: "signup",
          login_hint: email || undefined
        }
      }

      loginWithRedirect({
        appState: {
          returnTo: returnTo
        },
        authorizationParams
      })
    }
  }, [isAuthenticated, isLoading, loginWithRedirect, location.pathname, location.search])

  const deleteNotif = useCallback(
    (id: string) => {
      console.log("Deleting notification", id)
      setNotifs(notifs.filter((notif: any) => notif.id !== id))
    },
    [notifs]
  )

  const addNotif = useCallback(
    (notif: any) => {
      console.log("Adding notification", notif)
      setNotifs([...notifs, notif])
    },
    [notifs]
  )

  useEffect(() => {
    App.addListener("appUrlOpen", async ({ url }) => {
      console.log("appUrlOpen", url)

      // Handle Auth0 callback URLs
      if (url.includes("state") && (url.includes("code") || url.includes("error"))) {
        console.log("appUrlOpen auth callback", url)
        try {
          await handleRedirectCallback(url)

          mixpanel.track("Mobile App Login", {
            platform: "mobile",
            device: {
              platform: settings.IS_ANDROID ? "android" : settings.IS_IOS ? "ios" : "web",
              native_device_id: NATIVE_DEVICE_ID || null,
              is_native_app: settings.IS_NATIVE_APP
            }
          })

          console.log("closing browser")
          await Browser.close()

          return
        } catch (error) {
          Sentry.captureException(error, {
            extra: {
              url,
              isAuthenticated,
              isLoading,
              authInitiated: authInitiated.current,
              isNativeApp: settings.IS_NATIVE_APP,
              location: window.location.href
            }
          })
          console.log("error handling auth callback, closing browser and navigating to mobile", error)
          await Browser.close()
          navigate("/mobile")
          return
        }
      }

      // Handle general navigation URLs
      let pathToNavigate: string | null = null

      try {
        // Handle web URLs like https://app.getvibrato.com/calls/new
        if (url.startsWith("https://app.getvibrato.com/") || url.startsWith("https://app-staging.vibrato.live/")) {
          const urlObj = new URL(url)
          pathToNavigate = urlObj.pathname + urlObj.search + urlObj.hash
        }
        // Handle mobile deep link URLs like com.getvibrato.com.mobile://calls/new
        else if (url.startsWith("com.getvibrato.com.mobile://")) {
          const pathPart = url.replace("com.getvibrato.com.mobile://", "")
          // Handle case where path might not start with /
          pathToNavigate = pathPart.startsWith("/") ? pathPart : `/${pathPart}`
        }

        if (pathToNavigate) {
          console.log("navigating to", pathToNavigate)
          window.location.href = pathToNavigate
        }
      } catch (error) {
        console.error("Error parsing deep link URL:", error)
      }

      // Close browser for mobile deep links
      if (settings.IS_NATIVE_APP) {
        console.log("closing browser")
        await Browser.close()
      }
    })
  }, [handleRedirectCallback, navigate])

  useEffect(() => {
    if (isAuthenticated && !userSession) {
      setUserApiSession(
        getAccessTokenSilently,
        setVibratoClient,
        setUserSession,
        setShowCountryRestriction,
        setShowEmailVerification,
        setCcVerified,
        setShowIntroModal,
        navigate,
        setProcessingGift,
        setShowReferralRewardModal,
        setErrorPageMsg
      ).catch(console.error)
    }
  }, [isAuthenticated, getAccessTokenSilently, userSession])

  useReloadOnNewPlan(vibratoClient)

  const permissionStatus = useContext(PushTokenContext)
  useRegisterPushToken(vibratoClient, permissionStatus)

  // Add page view tracking
  useEffect(() => {
    try {
      mixpanel.track_pageview()
    } catch (err) {
      console.error("Failed to track page view", err)
    }
  }, [location])

  useEffect(() => {
    if (searchParams.get("show_pricing") == "true") {
      setShowUpgradeModal(true)
      setUpgradeModalInitialView("individual")
    }
    if (searchParams.get("show_biz_pricing") == "true") {
      setShowUpgradeModal(true)
      setUpgradeModalInitialView("business")
    }
    if (searchParams.get("onboard") === "true") {
      setShowIntroModal(true)
    }
    if (searchParams.get("show_referral_reward") === "true") {
      // Check if we've already shown this modal to this user
      // const storageKey = `referral_reward_modal_shown:${userSession?.email || "unknown"}`
      // const hasShownBefore = localStorage.getItem(storageKey) === "true"

      // if (!hasShownBefore) {
      setShowReferralRewardModal(true)
      // localStorage.setItem(storageKey, "true")
      // }
    }
  }, [searchParams, userSession])

  // useEffect(() => {
  //   if (!isAuthenticated || !userSession) {
  //     return
  //   }

  //   // @ts-ignore
  //   window.FrontChat("init", {
  //     chatId: "8e660b684cca99ad248fd82a9f2ba74a",
  //     useDefaultLauncher: false,
  //     email: userSession.chat_username,
  //     userHash: userSession.chat_hash,
  //     // shouldHideCloseButton: false,
  //     onInitCompleted: function () {
  //       setLoadedFrontChat(true)
  //     }
  //   })
  // }, [location, loadedFrontChat, isAuthenticated, userSession])

  useEffect(() => {
    if (!isAuthenticated || !userSession) {
      return
    }

    if (settings.ENV != "development") {
      LogRocket.init("jsfcnd/vibrato-prod")
      LogRocket.getSessionURL((sessionURL) => {
        Sentry.setTag("logrocket_session_url", sessionURL)
      })
    }
  }, [isAuthenticated, userSession])

  // Update global email context when userSession changes
  useEffect(() => {
    if (!userSession) {
      return
    }

    if (userSession?.email) {
      setEmail(userSession.email)
    }
    if (userSession?.id) {
      setUserId(userSession.id)
    }
  }, [userSession])

  // const showFrontChat = () => {
  //   console.log("showing front chat")
  //   if (loadedFrontChat) {
  //     // @ts-ignore
  //     window.FrontChat("show")
  //   }
  // }

  if (location.pathname === "/logout") {
    console.log("Logout")
    return <Logout />
  }

  if (location.pathname === "/mobile") {
    console.log("Mobile")
    if (!isAuthenticated && !isLoading) {
      return <MobileSplashPage isAuthenticated={isAuthenticated} isLoading={isLoading} />
    }

    navigate("/dashboard")
  }

  if (isLoading || processingGift) {
    if (settings.SHOW_NATIVE_APP) {
      return <LoadingMobileLogin isAuthenticated={isAuthenticated} />
    }

    return <LoadingFullPage />
  }

  if (location.pathname === "/signup") {
    console.log("Signup")
    if (isAuthenticated) {
      navigate("/dashboard")
    }

    if (settings.IS_NATIVE_APP) {
      return <LoadingMobileLogin isAuthenticated={false} />
    }

    return <LoadingFullPage />
  }

  if (error || errorPageMsg) {
    console.error("Auth0 error", error)
    Sentry.captureException(error, {
      extra: {
        errorPageMsg,
        error
      }
    })

    return <ErrorPage errorMsg={errorPageMsg} />
  }

  if (isAuthenticated && showEmailVerification && vibratoClient !== null) {
    return (
      <VibratoClientContext.Provider value={vibratoClient}>
        <EmailNotVerified />
      </VibratoClientContext.Provider>
    )
  }

  if (isAuthenticated && showCountryRestriction) {
    return <CountryRestriction />
  }

  if (isAuthenticated && userSession && vibratoClient) {
    // if (showVerifyPhoneNumberModal) {
    //   return (
    //     <UpdateUserContext.Provider value={setUserSession}>
    //       <UserContext.Provider value={userSession}>
    //         <VibratoClientContext.Provider value={vibratoClient}>
    //           <VerifyPhoneNumberModal
    //             show={showVerifyPhoneNumberModal}
    //             setShow={setShowVerifyPhoneNumberModal}
    //             onClose={() => {}}
    //             onPhoneNumberVerified={null}
    //           />
    //         </VibratoClientContext.Provider>
    //       </UserContext.Provider>
    //     </UpdateUserContext.Provider>
    //   )
    // }

    const navSize = 45

    return (
      <UpdateUserContext.Provider value={setUserSession}>
        <UserContext.Provider value={userSession}>
          <VibratoClientContext.Provider value={vibratoClient}>
            <AppleInAppPurchasesContext.Provider value={appleInAppPurchases}>
              <NotificationsContext.Provider value={addNotif}>
                <UpgradeModalContext.Provider
                  value={{
                    showModal: showUpgradeModal,
                    setShowModal: setShowUpgradeModal,
                    error: upgradeModalError,
                    setError: setUpgradeModalError,
                    title: upgradeModalTitle,
                    setTitle: setUpgradeModalTitle,
                    header: upgradeModalHeader,
                    setHeader: setUpgradeModalHeader,
                    details: upgradeModalDetails,
                    setDetails: setUpgradeModalDetails,
                    initialView: upgradeModalInitialView,
                    setInitialView: setUpgradeModalInitialView
                  }}
                >
                  <IntroModalContext.Provider value={setShowIntroModal}>
                    <div className="pt-safe px-safe h-screen-safe max-w-screen w-screen bg-white font-sans font-light site-height">
                      <GlobalAlert />
                      <PricingTableModal
                        title={upgradeModalTitle}
                        showModal={showUpgradeModal}
                        setShowModal={setShowUpgradeModal}
                        header={upgradeModalHeader}
                        details={upgradeModalDetails}
                        errorMsg={upgradeModalError}
                        initialView={upgradeModalInitialView}
                        onClose={() => {
                          setUpgradeModalError("")
                          setUpgradeModalHeader(initialUpgradeModalHeader)
                          setUpgradeModalDetails(initialUpgradeModalDetails)
                          setUpgradeModalInitialView("individual")
                        }}
                      />

                      <div className={`h-full flex flex-col`}>
                        <div id="nav" className={`h-[${navSize}px] lg:h-0`}>
                          <Nav mobileSize={navSize} />
                        </div>
                        <ReferralRewardModal
                          show={showReferralRewardModal}
                          setShow={setShowReferralRewardModal}
                          onContinue={() => {
                            setShowVerifyPhoneNumberModal(true)
                          }}
                          onClose={() => {}}
                        />
                        <VerifyPhoneNumberModal
                          show={showVerifyPhoneNumberModal}
                          setShow={setShowVerifyPhoneNumberModal}
                          onClose={() => {
                            setShowVerifyPhoneNumberModal(false)
                          }}
                          onPhoneNumberVerified={null}
                        />
                        <IntroModal
                          show={showIntroModal}
                          setShow={setShowIntroModal}
                          onClose={() => {}}
                          email={userSession.email}
                        />
                        <div
                          className={`lg:ml-64 max-h-[calc(100%-${navSize}px)] h-full lg:max-h-full overflow-y-hidden`}
                          id="content"
                        >
                          <div className={`w-full h-full  flex flex-col items-center bg-gray-100 overflow-y-scroll`}>
                            {notifs.length > 0 && (
                              <div className="w-full h-full flex justify-center">
                                <div className="max-w-[400px] w-full">
                                  <Notifications notifs={notifs} deleteNotif={deleteNotif} />
                                </div>
                              </div>
                            )}
                            {children}
                          </div>
                        </div>

                        {/* Review Prompt - positioned as fixed overlay */}
                        <ReviewPrompt userSession={userSession} />
                        {/* </ReactPullToRefresh> */}
                      </div>
                    </div>
                  </IntroModalContext.Provider>
                </UpgradeModalContext.Provider>
              </NotificationsContext.Provider>
            </AppleInAppPurchasesContext.Provider>
          </VibratoClientContext.Provider>
        </UserContext.Provider>
      </UpdateUserContext.Provider>
    )
  }

  // Authentication handled in useEffect hooks above

  if (settings.SHOW_NATIVE_APP) {
    return <LoadingMobileLogin isAuthenticated={isAuthenticated} />
  }

  return <LoadingFullPage />
}

export function ChatToggleButton({ onClick }: { onClick: any }) {
  const [isVisible, setIsVisible] = useState(true)
  const location = useLocation()

  const handleToggleChat = () => {
    onClick()
  }

  // Don't show the chat button on any non-dashboard path
  if (!location.pathname.startsWith("/dashboard")) {
    return null
  }

  return (
    <>
      {isVisible && (
        <>
          <div className="fixed bottom-8 right-8 flex items-center p-2 shadow-lg rounded-full bg-gray-100 z-5">
            <div
              onClick={handleToggleChat}
              className="flex place-items-center justify-center rounded-full bg-purple-600 h-12 w-12"
            >
              <ChatBubble color="white" strokeWidth="2" height={24} width={24} />
            </div>
            <button onClick={() => setIsVisible(false)} className="text-gray-500 hover:text-gray-700 h-8 w-8">
              ✕
            </button>
          </div>
        </>
      )}
    </>
  )
}
